if (!customElements.get('zoom-dialog')) {
  customElements.define(
    'zoom-dialog',
    class ZoomDialog extends HTMLElement {
      constructor() {
        super();
        this.overlay = null;
        this.img = null;
        this.scale = 1;
        this.translateX = 0;
        this.translateY = 0;
        this.isDragging = false;
        this.startX = 0;
        this.startY = 0;
        this.lastTranslateX = 0;
        this.lastTranslateY = 0;

        this._onWheel = this._onWheel.bind(this);
        this._onMouseDown = this._onMouseDown.bind(this);
        this._onMouseMove = this._onMouseMove.bind(this);
        this._onMouseUp = this._onMouseUp.bind(this);
        this._onTouchStart = this._onTouchStart.bind(this);
        this._onTouchMove = this._onTouchMove.bind(this);
        this._onTouchEnd = this._onTouchEnd.bind(this);
        this._onKeyDown = this._onKeyDown.bind(this);
        this._onOverlayClick = this._onOverlayClick.bind(this);
        this._preventScroll = this._preventScroll.bind(this);
        this._initialized = false;
      }

      connectedCallback() {
        if (this._initialized) return;
        this.overlay = this.querySelector('.zoom-overlay');
        this.img = this.querySelector('.zoom-image');

        if (!this.overlay || !this.img) return;

        this.container = this.querySelector('.zoom-image-container');
        this.closeBtn = this.querySelector('.zoom-close-btn');

        this.container.addEventListener('wheel', this._onWheel, { passive: false });
        this.container.addEventListener('mousedown', this._onMouseDown);
        document.addEventListener('mousemove', this._onMouseMove);
        document.addEventListener('mouseup', this._onMouseUp);
        this.container.addEventListener('touchstart', this._onTouchStart, { passive: false });
        document.addEventListener('touchmove', this._onTouchMove, { passive: false });
        document.addEventListener('touchend', this._onTouchEnd);
        document.addEventListener('keydown', this._onKeyDown);
        this.closeBtn.addEventListener('click', () => this.close());
        this.overlay.addEventListener('click', this._onOverlayClick);
        this._initialized = true;
      }

      disconnectedCallback() {
        if (!this._initialized) return;
        this.container?.removeEventListener('wheel', this._onWheel);
        this.container?.removeEventListener('mousedown', this._onMouseDown);
        document.removeEventListener('mousemove', this._onMouseMove);
        document.removeEventListener('mouseup', this._onMouseUp);
        this.container?.removeEventListener('touchstart', this._onTouchStart);
        document.removeEventListener('touchmove', this._onTouchMove);
        document.removeEventListener('touchend', this._onTouchEnd);
        document.removeEventListener('keydown', this._onKeyDown);
        document.removeEventListener('touchmove', this._preventScroll);
        this._initialized = false;
      }

      open(index, event) {
        const mediaGallery = document.querySelector('media-gallery');
        if (!mediaGallery) return;

        const galleryMain = mediaGallery.querySelector('[data-gallery-main]');
        if (!galleryMain) return;

        const items = galleryMain.querySelectorAll('.gallery-main__item');
        let targetItem = null;

        if (index >= 0 && index < items.length) {
          targetItem = items[index];
        } else {
          targetItem = galleryMain.querySelector('.gallery-main__item.active');
        }

        if (!targetItem) return;

        const img = targetItem.querySelector('img');
        if (!img) return;

        const highResUrl = img.getAttribute('data_max_resolution') || img.src;
        this.img.alt = img.alt || '';

        if (this.parentElement !== document.body) {
          document.body.appendChild(this);
        }

        this._reset();
        this.img.src = img.src;

        const show = () => {
          this.overlay.classList.add('active');
          document.body.style.overflow = 'hidden';
          document.documentElement.style.overflow = 'hidden';
          document.addEventListener('touchmove', this._preventScroll, { passive: false });
        };

        if (this.img.decode) {
          this.img.decode().then(show).catch(show);
        } else {
          show();
        }

        if (highResUrl !== img.src) {
          const hiRes = new Image();
          hiRes.onload = () => { this.img.src = highResUrl; };
          hiRes.src = highResUrl;
        }
      }

      close() {
        this.overlay.classList.remove('active');
        document.body.style.overflow = '';
        document.documentElement.style.overflow = '';
        document.removeEventListener('touchmove', this._preventScroll);
        this._reset();
      }

      _reset() {
        this.scale = 1;
        this.translateX = 0;
        this.translateY = 0;
        this._applyTransform();
      }

      _applyTransform() {
        if (!this.img) return;
        this.img.style.transform = `translate(${this.translateX}px, ${this.translateY}px) scale(${this.scale})`;
      }

      _onWheel(e) {
        e.preventDefault();
        const delta = e.deltaY > 0 ? -0.2 : 0.2;
        this.scale = Math.min(5, Math.max(1, this.scale + delta));

        if (this.scale === 1) {
          this.translateX = 0;
          this.translateY = 0;
        }

        this._applyTransform();
      }

      _onMouseDown(e) {
        this._mouseDownX = e.clientX;
        this._mouseDownY = e.clientY;
        if (this.scale <= 1) return;
        e.preventDefault();
        this.isDragging = true;
        this.startX = e.clientX;
        this.startY = e.clientY;
        this.lastTranslateX = this.translateX;
        this.lastTranslateY = this.translateY;
      }

      _onMouseMove(e) {
        if (!this.isDragging) return;
        this.translateX = this.lastTranslateX + (e.clientX - this.startX);
        this.translateY = this.lastTranslateY + (e.clientY - this.startY);
        this._applyTransform();
      }

      _onMouseUp() {
        this.isDragging = false;
      }

      _wasDrag(e) {
        if (this._mouseDownX == null) return false;
        const dx = Math.abs(e.clientX - this._mouseDownX);
        const dy = Math.abs(e.clientY - this._mouseDownY);
        return dx > 3 || dy > 3;
      }

      _onTouchStart(e) {
        if (e.touches.length === 1 && this.scale > 1) {
          this.isDragging = true;
          this.startX = e.touches[0].clientX;
          this.startY = e.touches[0].clientY;
          this.lastTranslateX = this.translateX;
          this.lastTranslateY = this.translateY;
        } else if (e.touches.length === 2) {
          this.isDragging = false;
          this._pinchStartDist = this._getPinchDist(e.touches);
          this._pinchStartScale = this.scale;
        }
      }

      _onTouchMove(e) {
        if (e.touches.length === 1 && this.isDragging) {
          e.preventDefault();
          this.translateX = this.lastTranslateX + (e.touches[0].clientX - this.startX);
          this.translateY = this.lastTranslateY + (e.touches[0].clientY - this.startY);
          this._applyTransform();
        } else if (e.touches.length === 2 && this._pinchStartDist) {
          e.preventDefault();
          const dist = this._getPinchDist(e.touches);
          const ratio = dist / this._pinchStartDist;
          this.scale = Math.min(5, Math.max(1, this._pinchStartScale * ratio));
          if (this.scale <= 1) {
            this.translateX = 0;
            this.translateY = 0;
          }
          this._applyTransform();
        }
      }

      _onTouchEnd() {
        this.isDragging = false;
        this._pinchStartDist = null;
      }

      _getPinchDist(touches) {
        const dx = touches[0].clientX - touches[1].clientX;
        const dy = touches[0].clientY - touches[1].clientY;
        return Math.sqrt(dx * dx + dy * dy);
      }

      _onKeyDown(e) {
        if (e.key === 'Escape' && this.overlay.classList.contains('active')) {
          this.close();
        }
      }

      _onOverlayClick(e) {
        if (this._wasDrag(e)) return;
        if (e.target === this.overlay || e.target === this.container) {
          this.close();
        }
      }

      _preventScroll(e) {
        if (this.overlay.classList.contains('active')) {
          e.preventDefault();
        }
      }
    }
  );

  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-zoom-target]');
    if (!trigger) return;

    if (!trigger.classList.contains('product-media-container--zoomable')) return;

    const zoomDialog = document.querySelector(trigger.getAttribute('data-zoom-target'));
    if (!zoomDialog) return;

    const index = parseInt(trigger.getAttribute('data-zoom-index') || '0', 10);
    zoomDialog.open(index, e);
  });
}
